import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, PlayCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Fix 3: read-only dry-run of the subscription detection pipeline.
 *
 * Runs the full detector without writing and shows per-stage counts, so the
 * operator can see exactly where prod data shape breaks detection (which was
 * returning HTTP 500 in prod). If a stage fails it names the stage, the
 * offending document id, and the traceback.
 */
export function SubscriptionDetectDryrunPanel() {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('all');
  const [scope, setScope] = useState('entities');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    entitiesAPI.getAll().then((res) => {
      const list = res.data || [];
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setEntities(list);
    }).catch(() => {});
  }, []);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const params = {};
      if (entityId && entityId !== 'all') params.entity_id = entityId;
      else params.scope = scope;
      const { data } = await api.get('/admin/diagnostics/subscription-detect-dryrun', {
        params,
        timeout: 120000,
      });
      setResult(data);
      if (data.ok) toast.success('Dry-run complete (no writes)');
      else toast.error(`Dry-run failed at stage: ${data.error?.stage}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const stats = result?.stats;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PlayCircle className="h-4 w-4" />
          Subscription Detection Dry-Run
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Runs the full detection pipeline read-only and reports per-stage counts.
          Names the stage, offending document id, and traceback if a prod data
          shape breaks it. Writes nothing.
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="text-sm border rounded px-2 py-1.5 bg-background"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
          >
            <option value="all">All entities</option>
            {entities.map((ent) => (
              <option key={ent.id} value={ent.id}>{ent.name}</option>
            ))}
          </select>
          {entityId === 'all' && (
            <select
              className="text-sm border rounded px-2 py-1.5 bg-background"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <option value="entities">Business scope</option>
              <option value="personal">Personal scope</option>
            </select>
          )}
          <Button size="sm" onClick={run} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run dry-run'}
          </Button>
        </div>

        {result && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              {result.ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              )}
              <span className="font-medium">
                {result.ok ? 'Pipeline completed (read-only)' : 'Pipeline failed'}
              </span>
            </div>

            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div className="bg-muted/50 rounded p-2">
                  <div className="font-semibold mb-1">Rows read</div>
                  {Object.entries(stats.rows_read).map(([src, n]) => (
                    <div key={src} className="flex justify-between font-mono">
                      <span>{src}</span><span>{n.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-muted/50 rounded p-2">
                  <div className="font-semibold mb-1">Skipped (unparseable amount)</div>
                  {Object.entries(stats.skipped_unparseable).map(([src, n]) => (
                    <div key={src} className="flex justify-between font-mono">
                      <span>{src}</span>
                      <span className={n > 0 ? 'text-amber-600' : ''}>{n.toLocaleString()}</span>
                    </div>
                  ))}
                  {Object.entries(stats.skipped_sample_ids).some(([, ids]) => ids.length > 0) && (
                    <div className="mt-1 text-muted-foreground">
                      sample ids:{' '}
                      {Object.entries(stats.skipped_sample_ids).flatMap(([, ids]) => ids).slice(0, 6).map((id) => (
                        <span key={id} className="font-mono mr-1">{id}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-muted/50 rounded p-2 md:col-span-2 flex gap-4 font-mono">
                  <span>groups formed: {stats.groups_formed}</span>
                  <span>passing cadence: {stats.groups_passing_cadence}</span>
                  <span>would upsert: {stats.would_upsert}</span>
                </div>
              </div>
            )}

            {!result.ok && result.error && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-2 text-xs space-y-1">
                <div>
                  <Badge variant="destructive" className="text-[10px]">stage: {result.error.stage}</Badge>
                  {result.error.doc_id && (
                    <span className="ml-2 font-mono">doc: {result.error.doc_id}</span>
                  )}
                </div>
                <div className="font-mono">{result.error.message}</div>
                {result.error.traceback && (
                  <details>
                    <summary className="cursor-pointer text-muted-foreground">traceback</summary>
                    <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px] overflow-x-auto">{result.error.traceback}</pre>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
